import React, { useEffect, useState } from 'react';
import { ETabKeys, NSConsignment } from './Consignment.d';
import Tab from './Components/Tab';
import { RouteComponentProps } from 'react-router-dom';
import { IconContext } from 'react-icons';
import { ImArrowLeft2 } from 'react-icons/im';
import { GrFormDown, GrLocation } from 'react-icons/gr';
import { BsPlus } from 'react-icons/bs';
import classNames from 'classnames';
import Style from './Consignment.module.scss';
import { useFetchRacksCapacity } from './Hooks/useFetchRacksCapacity';
import { toast } from 'react-toastify';
import {
  ModalWarehouseCheckingSpace,
  ModalWarehouseSpaceFull,
} from './Components/ModalWarehouse';
import { connect } from 'react-redux';
import { UserDataInterface } from 'interface/reducers/auth';
import { useCheckUserRoles } from './Hooks/useCheckUserRoles';
import { useModalSelectLocation } from './Hooks/useModalSelectLocation';
import {
  doFetchCurrencyRate,
  doFetchProfile,
  doRefreshToken,
  doUpdateUserProfile,
  updateUserCountry,
} from 'actions/authAction';
import { doFetchCountries, doFetchProvinces } from 'actions/postalCodeAction';
import ModalSelectLocation from './Components/ModalSelectLocation';
import {
  CountriesStateInterface,
  ProvinceStateInterface,
} from 'interface/reducers/postalCode';
import { useModalSellerVerification } from './Hooks/useModalSellerVerification';
import ModalSellerVerification from './Components/ModalSellerVerification/ModalSellerVerification';
import Unverified from './Components/Unverified';
import {
  ConsignmentRootDataContext,
  ConsignmentRootDataInstance,
} from './Contexts/ConsignmentRootData';
import Summary from './Components/Summary';

interface IConsignmentProps
  extends RouteComponentProps,
    NSConsignment.IModalSelectLocationActionProps {
  userData: UserDataInterface;
  countries: CountriesStateInterface;
  provinces: ProvinceStateInterface;
  currency: string;
  ratio: number;
}

const ConsignmentRootDataProvider = ({ children }) => {
  const [instance] = useState(() => ConsignmentRootDataInstance);
  const [, setUpdateFlag] = useState(0);

  useEffect(() => {
    instance._callback = () => {
      setUpdateFlag((prev) => prev + 1);
    };

    return () => {
      setUpdateFlag(0);
    };
  }, []);

  return (
    <ConsignmentRootDataContext.Provider value={{ value: instance }}>
      {children}
    </ConsignmentRootDataContext.Provider>
  );
};

const Consignment: React.FC<IConsignmentProps> = (props) => {
  const {
    history,
    userData,
    countries,
    provinces,
    currency,
    ratio,
    fetchCountries,
    fetchProvinces,
    updateProfile,
    updateUserCountry,
    fetchCurrencyRate,
    refreshToken,
    fetchProfile,
  } = props;
  const [tabActiveKey, setTabActiveKey] = useState<NSConsignment.TTabKeys>(
    ETabKeys.Pending,
  );
  const [isOpenModalCheckingSpace, setIsOpenModalCheckingSpace] =
    useState<boolean>(false);
  const [isOpenModalSpaceFull, setIsOpenModalSpaceFull] =
    useState<boolean>(false);

  const { isUnverified, isSuspended } = useCheckUserRoles({ userData });
  const { fetching: fetchRacks } = useFetchRacksCapacity({ pause: true });
  const modalSelectLocation = useModalSelectLocation({
    userData,
    countries,
    provinces,
    fetchProvinces,
    fetchCountries,
    updateProfile,
    updateUserCountry,
    fetchCurrencyRate,
    refreshToken,
    fetchProfile,
  });
  const modalSellerVerification = useModalSellerVerification();

  const onClickBack = () => {
    history.push({ pathname: `/user_dashboard` });
  };

  const onClickConsignProduct = async () => {
    if (isSuspended) {
      return;
    }

    const isNeedVerification =
      !userData.seller_verification &&
      userData.country_code === 'IDN' &&
      userData.roles.includes('seller');

    if (isNeedVerification) {
      const {
        isOpenExistingModal,
        toggleSellerVerification,
        setIsOpenExistingModal,
      } = modalSellerVerification;

      toggleSellerVerification();
      setIsOpenExistingModal(!isOpenExistingModal);
      return;
    }

    setIsOpenModalCheckingSpace(true);
    const res = await fetchRacks();
    if (!res.isSuccess) {
      toast.error(
        res.message ?? 'Oopss, please try again later or contact our support.',
        {
          position: 'top-right',
          autoClose: 5000,
          closeOnClick: true,
          pauseOnHover: true,
        },
      );
    }

    const availableCount = res.data?.available ?? 0;
    const isAvailable = availableCount > 0;

    if (!isAvailable) {
      setIsOpenModalCheckingSpace(false);
      setIsOpenModalSpaceFull(true);
      return;
    }

    setTimeout(() => {
      setIsOpenModalCheckingSpace(false);
      history.push({ pathname: '/consignment-request' });
    }, 1000);
  };

  if (isUnverified) {
    return <Unverified />;
  }

  return (
    <ConsignmentRootDataProvider>
      <div className={classNames(Style['consignment'], 'selling-container')}>
        <div className={'title-container'}>
          <div className={'back-button'} onClick={onClickBack}>
            <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
              <div>
                <ImArrowLeft2 />
              </div>
            </IconContext.Provider>
          </div>
          <span className={'title'}>Consignment Dashboard</span>
          <div
            className={'shipping-container'}
            onClick={modalSelectLocation.toggleModalSellerLocation}
          >
            <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
              <div>
                <GrLocation />
              </div>
            </IconContext.Provider>
            <span className={'ship-to'}>
              Seller location
              <span className={'ship-to-location'}>
                {modalSelectLocation.sellerLocation
                  ? modalSelectLocation.sellerLocationDisplaying
                  : '-'}
              </span>
            </span>
            <IconContext.Provider value={{ color: '#242424', size: '10px' }}>
              <div>
                <GrFormDown />
              </div>
            </IconContext.Provider>
          </div>
        </div>

        <Summary currency={currency} ratio={ratio} />

        <div
          className={classNames(
            Style['seller-action-container'],
            'seller-action-container',
          )}
        >
          <div
            className={'sell-request'}
            style={{ backgroundColor: '#242424' }}
            onClick={onClickConsignProduct}
          >
            <IconContext.Provider value={{ color: '#242424', size: '15px' }}>
              <div>
                <BsPlus style={{ color: 'white' }} />
              </div>
            </IconContext.Provider>
            <p style={{ color: 'white' }}>Consign a Product</p>
          </div>
        </div>

        <Tab activeKey={tabActiveKey} onChangeTab={setTabActiveKey} />

        <ModalWarehouseCheckingSpace isOpen={isOpenModalCheckingSpace} />
        <ModalWarehouseSpaceFull
          isOpen={isOpenModalSpaceFull}
          onClose={() => setIsOpenModalSpaceFull(false)}
        />
        <ModalSelectLocation
          userData={userData}
          isOpen={modalSelectLocation.isOpenModal}
          toggleModalSellerLocation={
            modalSelectLocation.toggleModalSellerLocation
          }
          countrySelected={modalSelectLocation.countrySelected}
          countryList={modalSelectLocation.countryList}
          provinceSelected={modalSelectLocation.provinceSelected}
          provinceList={modalSelectLocation.provinceList}
          locationSubmitted={modalSelectLocation.locationSubmitted}
          handleCountryChange={modalSelectLocation.handleCountryChange}
          handleProvinceChange={modalSelectLocation.handleProvinceChange}
          onSubmitLocation={modalSelectLocation.onSubmitLocation}
          errorLocation={modalSelectLocation.errorLocation}
        />

        {modalSellerVerification.isOpenSellerVerification && (
          <ModalSellerVerification
            isOpenSellerVerification={
              modalSellerVerification.isOpenSellerVerification
            }
            isOpenExistingModal={modalSellerVerification.isOpenExistingModal}
            phone={modalSellerVerification.phone}
            setPhone={modalSellerVerification.setPhone}
            setIsOpenExistingModal={
              modalSellerVerification.setIsOpenExistingModal
            }
            toggleSellerVerification={
              modalSellerVerification.toggleSellerVerification
            }
            toggleExistingModal={modalSellerVerification.toggleExistingModal}
          />
        )}
      </div>
    </ConsignmentRootDataProvider>
  );
};

const mapStateToProps = (state: NSConsignment.TStateToProps) => {
  return {
    userData: state.dataAuth.userdata,
    countries: state.dataPostalCode.countries,
    provinces: state.dataPostalCode.provinces,
    currency: state.dataAuth.userCurrency.currency,
    ratio: state.dataAuth.userCurrency.ratio,
  };
};

const mapDispatchToProps = (dispatch: (arg0: any) => any) => {
  return {
    fetchProvinces: (country: string) => dispatch(doFetchProvinces(country)),
    fetchCountries: () => dispatch(doFetchCountries()),
    updateProfile: (payload) => dispatch(doUpdateUserProfile(payload)),
    updateUserCountry: (payload) => dispatch(updateUserCountry(payload)),
    fetchProfile: () => dispatch(doFetchProfile()),
    fetchCurrencyRate: (payload) => dispatch(doFetchCurrencyRate(payload)),
    refreshToken: () => dispatch(doRefreshToken()),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Consignment);
